/**
 * Toast Notification System — components only
 *
 * E.14 (2026-04-29): the ToastContext + useToast hook moved to useToast.ts
 * so this file exports only React components (Vite Fast Refresh requirement).
 */
import React, { useState, useCallback, useRef, useContext } from 'react';
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import clsx from 'clsx';
import { Toast, ToastType, ToastContext } from './useToast';

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback(
    (type: ToastType, message: string, duration: number = 5000): string => {
      const id = `toast-${toastIdRef.current++}`;
      const toast: Toast = { id, type, message, duration };

      setToasts((prev) => [...prev, toast]);

      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
      }

      // Phase B (E.21.37) Reviewer 2 Concern 1: return the ID so callers
      // can dismiss the toast explicitly before its natural expiration.
      // useAutoRebuildOnSave holds the "Rebuilding…" toast for 60s and
      // dismisses it as soon as terminal build status arrives — pre-fix
      // addToast returned void and the dismiss call was a silent no-op.
      return id;
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

function ToastContainer() {
  const context = useContext(ToastContext);
  if (!context) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm">
      {context.toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={() => context.removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const icons = {
    success: <CheckCircle2 className="w-5 h-5 text-vibe-success" />,
    error: <AlertCircle className="w-5 h-5 text-vibe-error" />,
    warning: <AlertTriangle className="w-5 h-5 text-vibe-warning" />,
    info: <Info className="w-5 h-5 text-vibe-accent" />,
  };

  const bgColors = {
    success: 'bg-vibe-success/10 border-vibe-success/20',
    error: 'bg-vibe-error/10 border-vibe-error/20',
    warning: 'bg-vibe-warning/10 border-vibe-warning/20',
    info: 'bg-vibe-accent/10 border-vibe-accent/20',
  };

  return (
    <div
      className={clsx(
        'flex items-start gap-3 p-4 rounded-lg border shadow-lg animate-slide-in',
        bgColors[toast.type]
      )}
      role="alert"
    >
      {icons[toast.type]}
      <p className="flex-1 text-sm text-vibe-text">{toast.message}</p>
      <button
        onClick={onDismiss}
        className="text-vibe-text-secondary hover:text-vibe-text"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
